import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import RefEntryModal from "./RefEntryModal";

/**
 * PayeesList — CRUD page for the `payee` table.
 * List view mirrors Codes (2 filterable columns: Payee ID + Name).
 * The edit modal shows the full record (id, name, address, city,
 * state, tel) via a fresh Payee_Rea fetch.
 */
const PAGE_SIZE = 25;

export default function PayeesList({ profile, canWrite }) {
  const isAdmin = canWrite;
  const [payees, setPayees] = useState(null);
  const [err, setErr] = useState("");
  const [idFilter, setIdFilter] = useState("");
  const [nmFilter, setNmFilter] = useState("");
  const [modal, setModal] = useState(null);   // { mode, row, loading? }
  const [page, setPage] = useState(0);

  const load = () => {
    setPayees(null);
    api.listPayees()
      .then((rows) => setPayees(rows || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message || "Failed to load payees"));
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!payees) return null;
    const i = idFilter.trim().toLowerCase();
    const n = nmFilter.trim().toLowerCase();
    if (!i && !n) return payees;
    return payees.filter((r) => {
      const id = String(r.pye_id ?? "").toLowerCase();
      const nm = String(r.pye_nm ?? "").toLowerCase();
      return (!i || id.includes(i)) && (!n || nm.includes(n));
    });
  }, [payees, idFilter, nmFilter]);

  const anyFilter = idFilter || nmFilter;
  const clearFilters = () => { setIdFilter(""); setNmFilter(""); };
  useEffect(() => { setPage(0); }, [idFilter, nmFilter]);
  const total = rows?.length ?? 0;
  const pagedRows = useMemo(
    () => (rows ? rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE) : null),
    [rows, page]
  );
  const filterInputStyle = {
    width: "100%", padding: "4px 8px", fontSize: 13,
    border: "1px solid #555", borderRadius: 4, fontWeight: "normal",
  };

  const fields = [
    { name: "pye_id", label: "Payee ID", maxLength: 8, required: true,
      pk: true, hint: "max 8 chars" },
    { name: "pye_nm", label: "Name", maxLength: 50, required: true,
      hint: "max 50 chars", gridSpan: 2 },
    { name: "pye_addr", label: "Address", maxLength: 50, gridSpan: 2 },
    { name: "pye_city", label: "City", maxLength: 50 },
    { name: "pye_state", label: "State", maxLength: 2, hint: "2-letter code" },
    { name: "pye_tel", label: "Phone", maxLength: 15, gridSpan: 2 },
  ];

  const openRow = (r) => {
    // Payee_Lst now returns the full record — no extra fetch needed.
    setModal({ mode: "edit", row: r });
  };

  const handleSave = async (payload) => {
    if (modal.mode === "create") {
      await api.createPayee(payload);
    } else {
      const { pye_id: _pye_id, ...rest } = payload;
      await api.updatePayee(modal.row.pye_id, rest);
    }
    setModal(null); load();
  };
  const handleDelete = async () => {
    await api.deletePayee(modal.row.pye_id);
    setModal(null); load();
  };

  return (
    <section data-testid="payees-section">
      <p className="section-title">Payees</p>
      <div className="card list-card txn-card" data-testid="payees-card">
        <div className="list-header txn-header">
          <div className="list-header-left txn-header-left">
            <span className="list-count count" data-testid="payees-count">
              {payees
                ? `${(rows || []).length} of ${payees.length} payee${payees.length === 1 ? "" : "s"}`
                : "Loading…"}
            </span>
            {anyFilter && (
              <button className="btn-ghost btn-sm" onClick={clearFilters}
                      data-testid="payees-flt-clear">Clear filters</button>
            )}
          </div>
          {isAdmin && (
            <div className="list-header-right txn-header-right">
              <button className="btn-primary"
                      data-testid="payees-new-btn"
                      onClick={() => setModal({ mode: "create", row: {} })}
              >+ New payee</button>
            </div>
          )}
        </div>

        <div className="list-scroll txn-scroll" data-testid="payees-scroll">
          {err && <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>}
          {!rows ? (
            <div className="loading-row"><span className="spinner" /> Loading payees…</div>
          ) : (
            <table className="list-table list-table-compact txn-table txn-table-compact" data-testid="payees-table">
              <thead>
                <tr className="list-head-labels txn-head-labels">
                  <th style={{ width: "10%" }}>Payee ID</th>
                  <th style={{ width: "22%" }}>Name</th>
                  <th>Address</th>
                  <th style={{ width: "13%" }}>City</th>
                  <th style={{ width: "6%" }}>State</th>
                  <th style={{ width: "14%" }}>Phone</th>
                </tr>
                <tr className="list-head-filters txn-head-filters">
                  <th>
                    <input data-testid="payees-filter-id" type="text"
                      placeholder="Filter ID" value={idFilter}
                      onChange={(e) => setIdFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                  <th>
                    <input data-testid="payees-filter-name" type="text"
                      placeholder="Filter name" value={nmFilter}
                      onChange={(e) => setNmFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                  <th colSpan={4}></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "18px",
                                             color: "var(--muted, #666)" }}
                        data-testid="payees-empty">
                      No payees match the current filters.
                    </td>
                  </tr>
                ) : (
                  pagedRows.map((r) => {
                    const missing = !(r.pye_addr || r.pye_city || r.pye_state || r.pye_tel);
                    return (
                      <tr key={r.pye_id}
                          data-testid={`payees-row-${r.pye_id}`}
                          onClick={() => openRow(r)}
                          style={{ cursor: "pointer" }}
                          className={missing ? "row-warn" : undefined}>
                        <td style={{ fontFamily: "var(--font-mono, monospace)" }}>{r.pye_id}</td>
                        <td>{r.pye_nm}</td>
                        <td className={r.pye_addr ? undefined : "muted"}>{r.pye_addr || "—"}</td>
                        <td className={r.pye_city ? undefined : "muted"}>{r.pye_city || "—"}</td>
                        <td className={r.pye_state ? undefined : "muted"}>{r.pye_state || "—"}</td>
                        <td className={r.pye_tel ? undefined : "muted"}>{r.pye_tel || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div className="list-pager" data-testid="payees-pager">
            <span className="list-pager-info" data-testid="payees-pager-info">
              Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + PAGE_SIZE, total)} of {total}
            </span>
            <div className="list-pager-btns">
              <button className="btn-ghost" data-testid="payees-prev"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</button>
              <button className="btn-ghost" data-testid="payees-next"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}>Next ›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <RefEntryModal
          testIdPrefix="payees-modal"
          mode={modal.mode}
          title={modal.mode === "create" ? "New payee" : `Edit payee ${modal.row.pye_id}`}
          fields={fields}
          initial={modal.row}
          canWrite={isAdmin}
          onSave={handleSave}
          onDelete={modal.mode === "edit" ? handleDelete : undefined}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
}
